/* shopflo-neo:v1.2.0 — ShopPass-only port (lines 605+ of the reference shopflo-script.js) */
const ShopfloAccountsConfig = {
  ready: {
    intervalMs: 150,
    maxAttempts: 20,
    onTimeout: 'warn',
  },

  session: {
    isSessionKey: 'flo_isShopfloSession',
    isLogoutKey: 'FLO_SSO_IS_LOGOUT',
  },

  iframe: {
    sourceId: 'flo-shopify-login-iframe',
    mobileBreakpoint: 767, // bundle: innerWidth < 768 = mobile sheet (was 799 -> dead zone at 768-799px)
  },

  debug: false,
};

function readShopfloSessionStorage(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    console.warn('[shopflo-accounts]', 'sessionStorage.getItem(\'' + key + '\') threw - treating as absent.', e);
    return null;
  }
}

function writeShopfloSessionStorage(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('[shopflo-accounts]', 'sessionStorage.setItem(\'' + key + '\') threw - continuing anyway.', e);
    return false;
  }
}

function resolveShopfloAuthState() {
  const { isSessionKey, isLogoutKey } = ShopfloAccountsConfig.session;
  const hasSession = readShopfloSessionStorage(isSessionKey) === 'true';
  const isLoggingOut = readShopfloSessionStorage(isLogoutKey) === 'true';
  const state = hasSession && !isLoggingOut ? 'logged-in' : 'logged-out';
  return { hasSession, isLoggingOut, state };
}

function seedShopfloSessionFlagsIfMissing() {
  const { isSessionKey, isLogoutKey } = ShopfloAccountsConfig.session;
  const hasEitherFlag =
    readShopfloSessionStorage(isSessionKey) !== null || readShopfloSessionStorage(isLogoutKey) !== null;
  if (hasEitherFlag) return;

  writeShopfloSessionStorage(isSessionKey, 'true');
  writeShopfloSessionStorage(isLogoutKey, 'true');
}
seedShopfloSessionFlagsIfMissing();

window.isThemeFloLoggedIn = function () {
  return resolveShopfloAuthState().state === 'logged-in';
};

class ShopfloAccounts extends HTMLElement {
  connectedCallback() {
    if (this._shopfloInitialized) return;
    this._shopfloInitialized = true;

    this._log('connectedCallback: initializing slot=' + this._resolveSlot());

    this._onPageShow = this._onPageShow.bind(this);
    window.addEventListener('pageshow', this._onPageShow);

    this._applyAuthState(this._resolveSessionState());

    this._setupHeaderIconTrigger();
    this._setupOverlayTrigger();
    this._setupDrawerItemTriggers();

    this._log('connectedCallback: setup complete, triggers found:', {
      'header-icon': this.querySelectorAll('[data-flo-trigger="header-icon"]').length,
      'close-drawer': this.querySelectorAll('[data-flo-trigger="close-drawer"]').length,
      'account-login': this.querySelectorAll('[data-flo-trigger="account-login"]').length,
      'account-logout': this.querySelectorAll('[data-flo-trigger="account-logout"]').length,
    });
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this._onPageShow);
  }

  _log(...args) {
    if (ShopfloAccountsConfig.debug) console.log('[shopflo-accounts]', ...args);
  }

  _onPageShow(event) {
    if (!event.persisted) return;
    const wasOpen = this.querySelector(
      '[data-flo-state="drawer"][data-flo-visible="true"], [data-flo-state="drawer-iframe"][data-flo-visible="true"]'
    );
    if (!wasOpen) return;
    this._closeDrawer();
  }

  _globalsReady(fnNames) {
    return fnNames.every((name) => typeof window[name] === 'function');
  }

  _waitForGlobals(fnNames) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      if (!fnNames.length || this._globalsReady(fnNames)) {
        resolve(true);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (this._globalsReady(fnNames)) {
          clearInterval(timer);
          resolve(true);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(false);
        }
      }, readyConfig.intervalMs);
    });
  }

  _waitForElement(id) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      const existing = document.getElementById(id);
      if (existing) {
        resolve(existing);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        const el = document.getElementById(id);
        if (el) {
          clearInterval(timer);
          resolve(el);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(null);
        }
      }, readyConfig.intervalMs);
    });
  }

  _readSessionStorage(key) {
    return readShopfloSessionStorage(key);
  }

  _writeSessionStorage(key, value) {
    return writeShopfloSessionStorage(key, value);
  }

  _resolveSessionState() {
    const { hasSession, isLoggingOut, state } = resolveShopfloAuthState();
    this._log('_resolveSessionState:', { hasSession, isLoggingOut, state });
    return state;
  }

  _applyAuthState(state) {
    const isLoggedIn = state === 'logged-in';
    this.querySelectorAll('[data-flo-state="login-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'false' : 'true');
    });
    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'true' : 'false');
    });
  }

  _setupHeaderIconTrigger() {
    const triggers = this.querySelectorAll('[data-flo-trigger="header-icon"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        this._log('header-icon clicked');
        this._handleHeaderIconClick(event);
      });
    });
  }

  _setupOverlayTrigger() {
    const triggers = this.querySelectorAll('[data-flo-trigger="close-drawer"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this._log('close-drawer (overlay) clicked');
        this._closeDrawer();
      });
    });
  }

  _setupDrawerItemTriggers() {
    this.querySelectorAll('[data-flo-trigger="account-login"]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        this._log('account-login clicked, typeof window.handleShopifyLogin =', typeof window.handleShopifyLogin);
        this._callGlobalWhenReady('handleShopifyLogin', [event, '/account']);
        this._handleAccountIframeFlow();
      });
    });

    this.querySelectorAll('[data-flo-trigger="account-logout"]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this._log('account-logout clicked');
        this._writeSessionStorage(ShopfloAccountsConfig.session.isLogoutKey, 'true');
        window.location.reload();
      });
    });
  }

  _dispatchAccountEvent(name) {
    document.dispatchEvent(new CustomEvent('shopflo-event:' + name));
  }

  _callGlobalWhenReady(fnName, args) {
    if (typeof window[fnName] === 'function') {
      this._log('_callGlobalWhenReady: window.' + fnName + '() already available, calling now.');
      window[fnName].apply(window, args);
      return;
    }
    this._log('_callGlobalWhenReady: window.' + fnName + '() not ready yet, polling (up to ' + (ShopfloAccountsConfig.ready.maxAttempts * ShopfloAccountsConfig.ready.intervalMs) + 'ms)...');
    this._waitForGlobals([fnName]).then((ok) => {
      if (!ok) {
        console.warn('[shopflo-accounts]', 'Timed out waiting for window.' + fnName + '(). The Shopflo bundle script (https://bridge.shopflo.com/js/shopflo.bundle.js) may have failed to load or hasn\'t defined this function - check the Network tab for that request.');
        return;
      }
      this._log('_callGlobalWhenReady: window.' + fnName + '() became available, calling now.');
      window[fnName].apply(window, args);
    });
  }

  _handleHeaderIconClick(event) {
    const state = this._resolveSessionState();
    this._applyAuthState(state);
    this._log('_handleHeaderIconClick: resolved state =', state);

    if (state === 'logged-in') {
      this._closeIframeFlow();
      const opening = this._isDrawerClosed();
      this._log('_handleHeaderIconClick: logged-in branch, opening =', opening);
      if (opening) {
        this._positionDrawer();
      }
      this._toggleState('drawer', event.currentTarget);
      this._setOverlayVisible(opening);
      this._dispatchAccountEvent(opening ? 'account-drawer-opened' : 'account-drawer-closed');
    } else {
      this._log('_handleHeaderIconClick: logged-out branch, deferring to window.handleDrawer()');
      this._dispatchAccountEvent('account-login-opened');
      this._callGlobalWhenReady('handleDrawer', []);
    }
  }

  _toggleState(state, triggerEl) {
    const targets = this.querySelectorAll('[data-flo-state="' + state + '"]');
    targets.forEach((el) => {
      const isVisible = el.getAttribute('data-flo-visible') === 'true';
      el.setAttribute('data-flo-visible', isVisible ? 'false' : 'true');
    });
    if (triggerEl) {
      const expanded = triggerEl.getAttribute('aria-expanded') === 'true';
      triggerEl.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
  }

  _isDrawerClosed() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    return !drawerEl || drawerEl.getAttribute('data-flo-visible') !== 'true';
  }

  _resolveSlot() {
    if (this.classList.contains('shopflo-accounts--secondary')) return 'secondary';
    if (this.classList.contains('shopflo-accounts--tertiary')) return 'tertiary';
    return 'primary';
  }

  _positionDrawer() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    const anchorEl = this.querySelector('[data-flo-state="account-icon"]');
    if (!drawerEl || !anchorEl) return;

    const slot = this._resolveSlot();
    const cacheKey =
      'shopflo_account_drawer_position_' +
      slot +
      '_' +
      this.dataset.drawerVertical +
      '_' +
      this.dataset.drawerHorizontal;
    let position = this._readCachedDrawerPosition(cacheKey);
    if (!position) {
      position = this._computeDrawerPosition(drawerEl, anchorEl);
      this._writeCachedDrawerPosition(cacheKey, position);
    }
    this._applyDrawerPosition(drawerEl, position);
  }

  _readCachedDrawerPosition(cacheKey) {
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.vertical === 'above' || parsed.vertical === 'below') && (parsed.horizontal === 'left' || parsed.horizontal === 'right' || parsed.horizontal === 'center')) {
        return parsed;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  _writeCachedDrawerPosition(cacheKey, position) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(position));
    } catch (e) {
    }
  }

  _measureDrawerSize(drawerEl) {
    drawerEl.style.setProperty('display', 'block', 'important');
    drawerEl.style.setProperty('visibility', 'hidden', 'important');
    const rect = drawerEl.getBoundingClientRect();
    drawerEl.style.removeProperty('display');
    drawerEl.style.removeProperty('visibility');
    return { width: rect.width, height: rect.height };
  }

  _computeDrawerPosition(drawerEl, anchorEl) {
    const anchorRect = anchorEl.getBoundingClientRect();
    const { width, height } = this._measureDrawerSize(drawerEl);
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;

    const preferredVertical = this.dataset.drawerVertical === 'above' ? 'above' : 'below';
    const preferredHorizontal =
      this.dataset.drawerHorizontal === 'left'
        ? 'left'
        : this.dataset.drawerHorizontal === 'center'
          ? 'center'
          : 'right';

    const spaceBelow = viewportH - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const spaceForPreferredVertical = preferredVertical === 'above' ? spaceAbove : spaceBelow;
    const spaceForOtherVertical = preferredVertical === 'above' ? spaceBelow : spaceAbove;
    const vertical = (spaceForPreferredVertical >= height || spaceForPreferredVertical >= spaceForOtherVertical)
      ? preferredVertical
      : (preferredVertical === 'above' ? 'below' : 'above');

    const spaceForRightAlign = anchorRect.right;
    const spaceForLeftAlign = viewportW - anchorRect.left;

    let horizontal;
    if (preferredHorizontal === 'center') {
      const anchorCenterX = anchorRect.left + anchorRect.width / 2;
      const centerFits =
        anchorCenterX >= width / 2 && viewportW - anchorCenterX >= width / 2;
      horizontal = centerFits ? 'center' : (spaceForLeftAlign >= spaceForRightAlign ? 'left' : 'right');
    } else {
      const spaceForPreferredHorizontal = preferredHorizontal === 'left' ? spaceForLeftAlign : spaceForRightAlign;
      const spaceForOtherHorizontal = preferredHorizontal === 'left' ? spaceForRightAlign : spaceForLeftAlign;
      horizontal = (spaceForPreferredHorizontal >= width || spaceForPreferredHorizontal >= spaceForOtherHorizontal)
        ? preferredHorizontal
        : (preferredHorizontal === 'left' ? 'right' : 'left');
    }

    return { vertical, horizontal };
  }

  _applyDrawerPosition(drawerEl, position) {
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-top', position.vertical === 'above');
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-left', position.horizontal === 'left');
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-center', position.horizontal === 'center');
  }

  _setOverlayVisible(visible) {
    this.querySelectorAll('[data-flo-state="account-drawer-overlay"]').forEach((el) => {
      el.setAttribute('data-flo-visible', visible ? 'true' : 'false');
    });
    this._setHeaderOverflowVisible(visible);
  }

  _setHeaderOverflowVisible(visible) {
    const raw = window.shopfloThemeConfig && window.shopfloThemeConfig.shopflo_account_overflow_fix_selectors;
    if (!raw) return;

    raw
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean)
      .forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          el.classList.toggle('sf-header-overflow-visible', visible);
        });
      });
  }

  _handleAccountIframeFlow() {
    this._waitForElement(ShopfloAccountsConfig.iframe.sourceId).then((iframeEl) => {
      if (!iframeEl) {
        console.warn(
          '[shopflo-accounts]',
          'Timed out waiting for #' + ShopfloAccountsConfig.iframe.sourceId + ' to appear after account-login click.'
        );
        return;
      }
      this._openIframeFlow(iframeEl);
    });
  }

  _openIframeFlow(realIframeEl) {
    this._dispatchAccountEvent('account-iframe-opened');

    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });

    const isMobile = window.matchMedia('(max-width: ' + ShopfloAccountsConfig.iframe.mobileBreakpoint + 'px)').matches;
    if (isMobile) {
      this._setOverlayVisible(false);
      return;
    }

    const src = realIframeEl.getAttribute('src');
    if (!src) {
      console.warn('[shopflo-accounts]', 'Real iframe #' + ShopfloAccountsConfig.iframe.sourceId + ' has no src yet.');
      return;
    }

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('src', src);
      iframeEl.setAttribute('data-flo-visible', 'true');
    });

    this._setOverlayVisible(true);

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-iframe-active', 'true');
    });

    this._startIframeSizeSync(realIframeEl);
  }

  _startIframeSizeSync(realIframeEl) {
    const applySize = () => {
      let height = realIframeEl.style.height;
      if (!height) {
        height = getComputedStyle(realIframeEl).height;
      }

      if (Math.round(parseFloat(height)) === 232) {
        height = '225px';
      }

      this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
        iframeEl.style.setProperty('--flo-sso-iframe-height', height);
      });
    };

    applySize();
    this._iframeSizeObserver = new MutationObserver(applySize);
    this._iframeSizeObserver.observe(realIframeEl, { attributes: true, attributeFilter: ['style'] });

    this._iframeSizeRealEl = realIframeEl;
    this._iframeTransitionEndHandler = (event) => {
      if (event.propertyName === 'height') applySize();
    };
    realIframeEl.addEventListener('transitionend', this._iframeTransitionEndHandler);
  }

  _stopIframeSizeSync() {
    if (this._iframeSizeObserver) {
      this._iframeSizeObserver.disconnect();
      this._iframeSizeObserver = null;
    }
    if (this._iframeSizeRealEl && this._iframeTransitionEndHandler) {
      this._iframeSizeRealEl.removeEventListener('transitionend', this._iframeTransitionEndHandler);
    }
    this._iframeSizeRealEl = null;
    this._iframeTransitionEndHandler = null;
  }

  _closeIframeFlow() {
    const wasOpen = this.querySelector('[data-flo-state="drawer-iframe"][data-flo-visible="true"]');

    this._stopIframeSizeSync();

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('data-flo-visible', 'false');
      iframeEl.setAttribute('src', 'about:blank');
      iframeEl.style.removeProperty('--flo-sso-iframe-height');
    });

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.removeAttribute('data-flo-iframe-active');
    });

    if (wasOpen) this._dispatchAccountEvent('account-iframe-closed');
  }

  _closeDrawer() {
    const wasDrawerOpen = !this._isDrawerClosed();
    this._closeIframeFlow();
    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });
    this._setOverlayVisible(false);
    this.querySelectorAll('[data-flo-trigger="header-icon"]').forEach((el) => {
      if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'false');
    });
    if (wasDrawerOpen) this._dispatchAccountEvent('account-drawer-closed');
  }
}

if (!customElements.get('shopflo-accounts')) {
  customElements.define('shopflo-accounts', ShopfloAccounts);
}